<template>
    <v-container fluid class="py-6">
        <!-- Top bar -->
        <v-row class="mb-6" align="center" justify="center">
            <v-col cols="12" md="10" lg="9" class="position-relative">
                <div class="d-flex align-center justify-center">
                    <v-btn variant="text" prepend-icon="mdi-arrow-left" class="position-absolute left-0"
                        @click="$router.back()">
                        Back
                    </v-btn>

                    <div class="text-center">
                        <div class="text-h5 font-weight-bold">
                            {{ isAdmin ? 'Listing Review' : 'Listing Details' }}
                        </div>
                        <div class="text-medium-emphasis">
                            {{
                                isAdmin
                                    ? 'Review all details before confirming or denying the listing.'
                                    : 'Manage your listing and message the client and us'
                            }}
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>

        <!-- Loading state -->
        <v-row v-if="isLoading" align="start" justify="center" class="mb-8">
            <v-col cols="12" md="3">
                <v-skeleton-loader type="card" />
            </v-col>
            <v-col cols="12" md="7">
                <v-skeleton-loader type="card" />
            </v-col>
        </v-row>

        <!-- Content -->
        <v-row v-else-if="currentListing" align="start" justify="center">
            <!-- Left: Agent / Meta -->
            <v-col cols="12" md="2" class="mb-6">
                <div class="text-h6 text-center font-weight-bold mb-3">Listing Agent</div>

                <v-card class="mb-4" elevation="2" rounded="xl">
                    <v-card-text class="text-center">
                        <v-avatar size="100" class="mb-3">
                            <v-img :src="agent.profilePicture || defaultProfilePicture" alt="Agent" />
                        </v-avatar>
                        <div class="text-subtitle-1 font-weight-medium mb-1">{{ agent.name || 'Agent Name' }}</div>
                        <div class="text-caption text-medium-emphasis mb-4">Cruise Posting Agent</div>

                        <div class="mb-2"><strong>Booking ID:</strong> {{ currentListing.booking_id || '—' }}</div>
                        <div class="mb-2"><strong>Created On:</strong> {{ formatDate(currentListing.created_at) }}</div>
                        <div class="mb-2" v-if="booking"><strong>Booked On:</strong> {{ formatDate(booking.created_at)
                            }}</div>
                        <div class="mb-2" v-else><strong>Booked On:</strong> Not yet booked</div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Right: Details / Tabs -->
            <v-col cols="12" md="9">
                <ListingStepper v-if="currentListing.current_step > 0" :active-step="currentListing.current_step - 1"
                    class="my-6" />

                <v-card elevation="3" rounded="xl" class="pa-6">
                    <div class="text-subtitle-1 text-medium-emphasis mb-1">
                        {{ currentListing.itinerary?.ship_name }}
                    </div>
                    <div class="text-h5 font-weight-bold mb-4">
                        {{ currentListing.itinerary?.title }}
                    </div>

                    <!-- Tabs -->
                    <v-tabs v-model="activeTab" class="mb-4" density="comfortable">
                        <v-tab value="details" prepend-icon="mdi-information-outline">Listing Details</v-tab>
                        <v-tab v-if="currentListing.current_step > 0" value="guest" prepend-icon="mdi-bed-outline">
                            Guest Info
                        </v-tab>
                        <v-tab v-if="currentListing.current_step > 0" value="guestChat" prepend-icon="mdi-chat-outline">
                            Guest Chat
                        </v-tab>
                        <v-tab v-if="currentListing.current_step > 0" value="documents"
                            prepend-icon="mdi-file-document-multiple-outline">
                            Documents
                        </v-tab>
                        <v-tab value="notifications" prepend-icon="mdi-bell-outline">
                            Notifications
                        </v-tab>
                        <v-tab v-if="currentListing.current_step > 0" value="payments" prepend-icon="mdi-credit-card-outline">
                            Payments
                        </v-tab>
                    </v-tabs>

                    <v-window v-model="activeTab">
                        <!-- Details -->
                        <v-window-item value="details">
                            <v-row class="gy-6">
                                <!-- Suite Details -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Suite Details</div>
                                    <div><strong>Code:</strong> {{ currentListing.selected_code || '—' }}</div>
                                    <div><strong>Description:</strong> {{ currentListing.description || '—' }}</div>
                                    <div><strong>Room Type:</strong> {{ currentListing.room_type || '—' }}</div>
                                    <div><strong>Guest Limit:</strong> {{ currentListing.guest_limit || '—' }}</div>
                                    <div><strong>Cabin Number:</strong> {{ currentListing.cabin_number || 'TBD' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Included Perks</div>
                                    <ul class="pl-4">
                                        <li v-for="perk in currentListing.perks || []" :key="perk">
                                            {{ formatPerk(perk) }}
                                        </li>
                                        <li v-if="!(currentListing.perks || []).length" class="text-medium-emphasis">
                                            None</li>
                                    </ul>
                                </v-col>

                                <!-- Booking Info -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Booking Info</div>
                                    <div><strong>Tips Included:</strong> {{ capitalize(currentListing.tips_included) ||
                                        '—' }}</div>
                                    <div><strong>Name Swaps Allowed:</strong> {{ capitalize(currentListing.name_swaps)
                                        || '—' }}</div>
                                    <div><strong>Promote Listing:</strong> {{ capitalize(currentListing.promote) || '—'
                                        }}</div>
                                    <div><strong>Notes:</strong> {{ currentListing.notes || 'None' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Cancellation Policy</div>
                                    <template v-if="hasCancellation">
                                        <ul class="pl-4">
                                            <li v-for="(c, idx) in currentListing.cancellation" :key="idx">
                                                {{ formatDate(c.date) || 'TBD' }} — {{ c.percentage || 'TBD' }}% penalty
                                            </li>
                                        </ul>
                                    </template>
                                    <div v-else class="text-medium-emphasis">No cancellation info provided.</div>
                                </v-col>

                                <!-- Pricing / Payout -->
                                <v-col cols="12" md="4">
                                    <div class="text-subtitle-1 font-weight-medium mb-3">Pricing</div>
                                    <div><strong>Cruise Fare:</strong> {{ money(currentListing.cruise_fare) }}</div>
                                    <div><strong>Taxes:</strong> {{ money(currentListing.taxes) }}</div>
                                    <div><strong>Deposit:</strong> {{ money(currentListing.deposit) }}</div>
                                    <div><strong>Total Cost:</strong> {{ currentListing.total_cost ?
                                        money(currentListing.total_cost) : 'TBD' }}</div>

                                    <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Payment & Payout</div>
                                    <div><strong>Final Payment Due:</strong> {{
                                        formatDate(currentListing.final_payment_date) || '—' }}</div>
                                    <div><strong>Remove Date:</strong> {{ formatDate(currentListing.remove_date) || '—'
                                        }}</div>
                                    <div><strong>Service Fee:</strong> {{ money(currentListing.service_fee) }}</div>
                                    <div class="text-success text-h6 font-weight-bold mt-1">Payout: {{
                                        money(estimatedPayout) }}</div>
                                </v-col>
                            </v-row>
                        </v-window-item>

                        <!-- Guest Info -->
                        <v-window-item value="guest">
                            <div class="text-subtitle-1 font-weight-medium mb-4">Guests</div>
                            <template v-if="booking?.guests?.list.length">
                                <v-card v-for="(guest, index) in booking.guests.list" :key="index" class="mb-4"
                                    variant="flat" rounded="lg">
                                    <v-card-text class="bg-grey-lighten-4 rounded">
                                        <div class="text-medium-emphasis mb-2">Guest {{ index + 1 }}</div>
                                        <div><strong>Name:</strong> {{ guest.firstName }} {{ guest.middleName }} {{
                                            guest.lastName }}</div>
                                        <div><strong>Gender:</strong> {{ guest.gender }}</div>
                                        <div><strong>Date of Birth:</strong> {{ formatDate(guest.dob) }}</div>
                                        <div><strong>Citizenship:</strong> {{ guest.citizenship }}</div>
                                        <div><strong>Language:</strong> {{ display(guest.language) }}</div>

                                        <div v-if="guest.selectedAdditional?.length" class="mt-2">
                                            <strong>Promo Qualifiers:</strong>
                                            <ul class="pl-4">
                                                <li v-for="(item, i) in guest.selectedAdditional" :key="i">{{ item }}
                                                </li>
                                            </ul>
                                        </div>
                                    </v-card-text>
                                </v-card>
                            </template>

                            <div class="text-subtitle-1 font-weight-medium mt-6 mb-3">Booking Preferences</div>
                            <v-row class="mb-3">
                                <v-col cols="12" md="6">
                                    <div><strong>Dining Time:</strong> {{ booking?.dining_time || '—' }}</div>
                                    <div><strong>Bed Preference:</strong> {{ booking?.bed || '—' }}</div>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <div><strong>Phone:</strong> {{ booking?.phone || '—' }}</div>
                                    <div><strong>Email:</strong> {{ booking?.email || '—' }}</div>
                                </v-col>
                            </v-row>

                            <div class="text-subtitle-1 font-weight-medium mb-2">Meta</div>
                            <div><strong>Booking ID:</strong> {{ booking?.id || '—' }}</div>
                            <div><strong>Listing ID:</strong> {{ booking?.listing_id || '—' }}</div>
                            <div><strong>Created At:</strong> {{ formatDate(booking?.created_at) || '—' }}</div>
                        </v-window-item>

                        <!-- Guest Chat -->
                        <v-window-item value="guestChat">
                            <div class="text-subtitle-1 font-weight-medium mb-3">Chat With Guest</div>
                            <div v-if="userId && guestID" class="ma-2">
                                <ChatRoom :userId="userId" :otherUserId="guestID" :listingID="currentListing.id" />
                            </div>
                            <div v-else class="text-medium-emphasis">Chat not available.</div>
                        </v-window-item>

                        <!-- Guest Documents -->
                        <v-window-item value="documents">
                            <v-row class="gy-4">
                                <!-- Files table -->
                                <v-col cols="12" md="12">
                                    <v-card elevation="1">
                                        <v-card-title>
                                            Booking Files
                                            <v-spacer />
                                            <small class="text-medium-emphasis">Showing {{ filesPageStart }}–{{
                                                filesPageEnd }} of {{
                                                    filesCount }}</small>
                                        </v-card-title>
                                        <v-card-text>
                                            <v-table>
                                                <thead>
                                                    <tr>
                                                        <th class="text-left">Type</th>
                                                        <th class="text-left">File Name</th>
                                                        <th class="text-left">Uploaded</th>
                                                        <th class="text-left">By</th>
                                                        <th class="text-left">Status</th>
                                                        <th class="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="f in files" :key="f.id">
                                                        <td class="text-capitalize">{{ humanDocType(f.doc_type) }}</td>
                                                        <td class="truncate-cell"
                                                            :title="f.original_name || f.storage_path">
                                                            {{ f.original_name || f.storage_path }}
                                                        </td>
                                                        <td>{{ formatDateTime(f.created_at) }}</td>
                                                        <td>{{ f.uploaded_by || '—' }}</td>

                                                        <td>
                                                            <v-chip v-bind="chipAttrsFor(f.status)" size="small"
                                                                class="font-weight-medium w-100 text-center"
                                                                variant="flat">
                                                                <v-icon :icon="docStatus[f.status].icon" start
                                                                    class="mx-1" />
                                                                {{ docStatus[f.status].label }}
                                                            </v-chip>
                                                        </td>

                                                        <td class="text-right">
                                                            <div class="d-flex flex-column align-center ga-2">
                                                                <!-- Row 1 -->
                                                                <v-btn size="small" variant="outlined" color="secondary"
                                                                    class="w-100" @click="openFile(f)">
                                                                    Open
                                                                </v-btn>

                                                                <v-btn v-if="f.status == 0" size="small"
                                                                    variant="outlined" color="green" class="w-100"
                                                                    @click="approveFile(f)">
                                                                    Approve
                                                                </v-btn>

                                                                <v-btn v-if="f.status == 0" size="small"
                                                                    variant="outlined" color="red" class="w-100"
                                                                    @click="denyFile(f)">
                                                                    Deny
                                                                </v-btn>
                                                            </div>
                                                        </td>

                                                    </tr>
                                                    <tr v-if="(files || []).length === 0">
                                                        <td colspan="5" class="text-center text-medium-emphasis py-6">No
                                                            files yet.</td>
                                                    </tr>
                                                </tbody>
                                            </v-table>

                                            <v-row class="mt-4" justify="end">
                                                <v-col cols="auto">
                                                    <v-pagination v-model="filesPage" :length="filesTotalPages"
                                                        :total-visible="5" prev-icon="mdi-chevron-left"
                                                        next-icon="mdi-chevron-right"
                                                        @update:modelValue="fetchBookingFiles" />
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-window-item>

                    </v-window>

                    <!-- Actions -->
                    <div class="text-center mt-8">
                        <template v-if="currentListing.current_step == 0">
                            <v-btn color="error" size="large" class="mr-3" @click="denyListing">Deny Listing</v-btn>
                            <v-btn color="success" size="large" @click="confirmListing">Activate Listing</v-btn>
                        </template>

                        <!-- Booking request -->
                        <template v-else-if="currentListing.current_step == 2">
                            <v-btn color="error" size="large" class="mr-3" @click="denyBooking">Deny Booking</v-btn>
                            <v-btn color="success" size="large" @click="approveBooking">Approve Booking</v-btn>
                        </template>

                        <!-- Confirmation -->
                        <template v-else-if="currentListing.current_step == 3">
                            <v-btn color="error" size="large" class="mr-3" @click="denyConfirmation">Deny
                                Confirmation</v-btn>
                            <v-btn color="success" size="large" @click="approveConfirmation">Approve
                                Confirmation</v-btn>
                        </template>

                        <!-- Payment -->
                        <template v-else-if="currentListing.current_step == 4">
                            <v-btn color="error" size="large" class="mr-3" @click="denyPayment">Deny
                                Payment Invoice</v-btn>
                            <v-btn color="success" size="large" @click="approvePayment">Approve
                                Payment Invoice</v-btn>
                        </template>

                        <template v-else-if="currentListing.current_step > 4">
                            <v-btn color="error" size="large" @click="removeListing">Remove Listing</v-btn>
                        </template>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-else align="center" justify="center">
            <v-col cols="12" md="8" class="text-center">
                <v-skeleton-loader type="text" class="mb-2" />
                <div class="text-medium-emphasis">Loading listing details…</div>
            </v-col>
        </v-row>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="2500">
            {{ snackbar.msg }}
        </v-snackbar>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'

import ChatRoom from '@/components/ChatRoom.vue'
import ListingStepper from '@/components/ListingStepper.vue'

import { useNotifier } from '@/composables/useNotifier'
const { sendNotification } = useNotifier()

/* ---------- store / route ---------- */
const store = useStore()
const route = useRoute()

const user = computed(() => store.state.user || null)
const userId = computed(() => user.value?.uid || null)
const isAdmin = computed(() => !!user.value?.isAdmin)

/* ---------- state ---------- */
const isLoading = ref(true)
const currentListing = ref(null)
const booking = ref(null)
const activeTab = ref('details')

const agent = ref({ name: '', profilePicture: '' })
const agentStats = ref({ pending: 0, listed: 0, booked: 0 })
const defaultProfilePicture = 'https://placehold.co/100'

const otherUserId = ref(null)
const guestID = ref(null)

const snackbar = ref({ show: false, msg: '', color: 'success' })

// Files / uploads
const files = ref([])
const filesCount = ref(0)
const filesPage = ref(1)
const filesPageSize = ref(10)
const filesTotalPages = ref(1)

// Doc types shown in the select
const docTypes = [
    { value: 'id', title: 'Government ID' },
    { value: 'passport', title: 'Passport' },
    { value: 'agreement', title: 'Signed Agreement' },
    { value: 'invoice', title: 'Invoice/Receipt' },
    { value: 'supplier_confirmation', title: 'Supplier Confirmation' },
    { value: 'other', title: 'Other' },
]

const docStatus = [
    { key: 'under_review', label: 'Under Review', color: 'orange', icon: 'mdi-account-clock' },
    { key: 'approved', label: 'Approved', color: 'green', icon: 'mdi-check-circle' },
    { key: 'denied', label: 'Denied', color: 'red', icon: 'mdi-alert-circle-outline' },
]

function chipAttrsFor(status) {
    const step = docStatus[status]
    return { color: step.color }
}

/* ---------- computed ---------- */
const hasCancellation = computed(() =>
    !!currentListing.value?.cancellation?.some(c => c?.date || c?.percentage)
)

const estimatedPayout = computed(() => {
    const total = Number(currentListing.value?.cruise_fare) || 0
    const fee = Number(currentListing.value?.service_fee) || 0
    return total - fee
})

const filesPageStart = computed(() => (filesPage.value - 1) * filesPageSize.value)
const filesPageEnd = computed(() => Math.min(filesPageStart.value + filesPageSize.value, filesCount.value || 0))

const bookingId = computed(() => booking.value?.id || null)

/* ---------- helpers ---------- */
function toast(msg, color = 'success') {
    snackbar.value = { show: true, msg, color }
}
function money(v) {
    const n = Number(v)
    if (Number.isNaN(n)) return '—'
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}
function formatDate(d) {
    if (!d) return '—'
    const dt = new Date(d)
    return isNaN(dt) ? '—' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}
function formatDateTime(d) {
    if (!d) return '—'
    const dt = new Date(d)
    return isNaN(dt) ? '—' : dt.toLocaleString()
}
function capitalize(v) {
    return v ? (String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase()) : '—'
}
function display(v) { return v || 'Not specified' }
function formatPerk(perk) {
    const map = {
        onboardCredit50: 'Onboard Credit - $50',
        onboardCredit100: 'Onboard Credit - $100',
        other: 'Other',
        specialtyDining: 'Specialty Dining',
        prepaidGratuity: 'Prepaid Gratuity',
        beveragePackage: 'Beverage Package',
        internetPackage: 'Internet Package',
        shoreExcursionCredit: 'Shore Excursion Credit'
    }
    return map[perk] || perk
}

function humanDocType(t) {
    const found = docTypes.find(d => d.value === t)
    return found ? found.title : (t || 'Unknown')
}

/* ---------- data loaders ---------- */
async function fetchListing() {
    isLoading.value = true
    try {
        const id = route.params.id
        const { data, error } = await supabase
            .from('listings')
            .select('*')
            .eq('id', id)
            .single()
        if (error) throw error

        currentListing.value = { id, ...data }
        otherUserId.value = data.uid

        const b = await fetchBookingByListingId(currentListing.value.id)
        booking.value = b
        guestID.value = b?.user_id || null

        await fetchBookingFiles() // initial page

        await Promise.all([
            getAgentInfo(data.uid),
            getAgentStats(data.uid),
        ])
    } catch (err) {
        console.error('Error fetching listing:', err)
        toast('Failed to load listing.', 'error')
    } finally {
        isLoading.value = false
    }
}

async function fetchBookingByListingId(listingId) {
    try {
        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .eq('listing_id', listingId)
            .limit(1)
            .single()
        if (error) throw error
        return data
    } catch (e) {
        // PGRST116 = no rows
        if (e?.code !== 'PGRST116') console.error('fetchBookingByListingId:', e)
        return null
    }
}

async function getAgentInfo(uid) {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('first_name,last_name,email,phone,profile_picture')
            .eq('uid', uid)
            .single()
        if (error) throw error

        agent.value = {
            name: [data.first_name, data.last_name].filter(Boolean).join(' ') || '—',
            email: data.email,
            phone: data.phone,
            profilePicture: data.profile_picture || defaultProfilePicture
        }
    } catch (e) {
        console.error('getAgentInfo:', e)
        agent.value = { name: '—', profilePicture: defaultProfilePicture }
    }
}

async function getAgentStats(uid) {
    try {
        const { data, error } = await supabase
            .from('listings')
            .select('status')
            .eq('uid', uid)
        if (error) throw error

        const stats = { pending: 0, listed: 0, booked: 0 }
            ; (data || []).forEach(l => {
                if (l.status === 'pending') stats.pending++
                else if (l.status === 'active') stats.listed++
                else if (l.status === 'booked') stats.booked++
            })
        agentStats.value = stats
    } catch (e) {
        console.error('getAgentStats:', e)
        agentStats.value = { pending: 0, listed: 0, booked: 0 }
    }
}

/* -------------------------------------------------------
   FILES (assumes table public.booking_files + bucket booking_docs)
------------------------------------------------------- */
const FILES_TABLE = 'booking_files'              // change if your table is named differently
const BUCKET_BOOKING_DOCS = 'booking_docs'       // change if your bucket is named differently

async function fetchBookingFiles(page = filesPage.value) {
    if (!bookingId.value) {
        files.value = []
        filesCount.value = 0
        filesTotalPages.value = 1
        return
    }

    const from = (page - 1) * filesPageSize.value
    const to = from + filesPageSize.value - 1

    try {
        const { data, count, error } = await supabase
            .from(FILES_TABLE)
            .select('*', { count: 'exact' })
            .eq('booking_id', bookingId.value)
            .order('created_at', { ascending: false })
            .range(from, to)

        if (error) throw error
        files.value = data || []
        filesCount.value = count ?? 0
        filesTotalPages.value = Math.max(1, Math.ceil((filesCount.value || 0) / filesPageSize.value))
    } catch (e) {
        console.error('fetchBookingFiles:', e)
        files.value = []
        filesCount.value = 0
        filesTotalPages.value = 1
    }
}

function normalizePath(p) {
    if (!p) return ''
    let s = String(p).trim().replace(/^\/+/, '') // no leading slash
    // if someone saved "booking_docs/..." in the DB, strip it (because .from('booking_docs') already sets the bucket)
    if (s.startsWith(`${BUCKET_BOOKING_DOCS}/`)) {
        s = s.slice(BUCKET_BOOKING_DOCS.length + 1)
    }
    return s
}

async function openFile(f) {
    try {
        if (!f?.storage_path) throw new Error('No storage path on record')

        const relPath = normalizePath(f.storage_path)

        // 1) Prefer a signed URL (works with private buckets)
        const { data, error } = await supabase
            .storage
            .from(BUCKET_BOOKING_DOCS)
            .createSignedUrl(relPath, 60) // seconds

        if (error) {
            console.error('[openFile] createSignedUrl error:', error)
            throw new Error(error.message || 'Could not create a signed URL')
        }

        const url = data?.signedUrl
        if (!url) {
            console.warn('[openFile] createSignedUrl returned no signedUrl.')
            throw new Error('Signed URL missing from response')
        }

        window.open(url, '_blank', 'noopener')
    } catch (e) {
        console.error('[openFile] ', e)
        toast(e?.message || 'File URL unavailable.', 'error')
    }
}

async function setFileStatus(file, newStatus) {
    if (!file?.id) {
        toast('Invalid file record.', 'error')
        return
    }

    try {
        const reviewer = user.value?.email || user.value?.uid || null

        const { data, error } = await supabase
            .from(FILES_TABLE)
            .update({
                status: newStatus,                          // 1 = approved, 2 = denied
                reviewed_by: reviewer,
                reviewed_at: new Date().toISOString(),
            })
            .eq('id', file.id)
            .select('id, status, reviewed_by, reviewed_at')
            .single()

        if (error) throw error

        // Optimistically update local list
        const i = files.value.findIndex(f => f.id === file.id)
        if (i !== -1) {
            files.value[i] = { ...files.value[i], ...data }
        }

        toast(newStatus === 1 ? 'File approved.' : 'File denied.')

        if (newStatus === 1)
            await sendNotification({ type: 'document_approved', listingId: currentListing.value.id, userId: currentListing.value.uid, meta: { docName: file.original_name, bookingId: currentListing.value.booking_id } })
        else
            await sendNotification({ type: 'document_denied', listingId: currentListing.value.id, userId: currentListing.value.uid, meta: { docName: file.original_name, reason: 'Does not meet requirements' } })

    } catch (e) {
        console.error('setFileStatus:', e)
        toast(`Update failed: ${e?.message || e}`, 'error')
    }
}

function approveFile(file) {
    return setFileStatus(file, 1)
}

function denyFile(file) {
    return setFileStatus(file, 2)
}


/* ---------- actions ---------- */
async function confirmListing() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'active' })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'active'

        await sendNotification({
            type: 'listing_approved',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Listing activated.')
    } catch (e) {
        console.error(e); toast('Failed to activate listing.', 'error')
    }
}

async function approveBooking() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'pendingagent', current_step: 3 })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'pendingagent'
        currentListing.value.current_step = 3

        await sendNotification({
            type: 'booking_approved',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Booking approved.')
    } catch (e) {
        console.error(e); toast('Failed to approve booking.', 'error')
    }
}

async function denyBooking() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'active' }) // back to market
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'active'
        toast('Booking denied; listing returned to market.')
    } catch (e) {
        console.error(e); toast('Failed to deny booking.', 'error')
    }
}

async function denyListing() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'denied' })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'denied'
        toast('Listing denied.')
    } catch (e) {
        console.error(e); toast('Failed to deny listing.', 'error')
    }
}

async function removeListing() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'removed' })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'removed'
        toast('Listing removed.')
    } catch (e) {
        console.error(e); toast('Failed to remove listing.', 'error')
    }
}

async function approveConfirmation() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'pendingagent', current_step: 4, awaiting_admin: false })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'pendingagent'
        currentListing.value.current_step = 4

        await sendNotification({
            type: 'approve_confirmation',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Confirmation approved.')
    } catch (e) {
        console.error(e); toast('Failed to approve confirmation.', 'error')
    }
}
async function denyConfirmation() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'pendingagent', current_step: 3, awaiting_admin: false })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'pendingagent'
        currentListing.value.current_step = 3

        await sendNotification({
            type: 'deny_confirmation',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Confirmation denied.')
    } catch (e) {
        console.error(e); toast('Failed to deny confirmation.', 'error')
    }
}
async function approvePayment() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'confirmed', current_step: 5, awaiting_admin: false })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'confirmed'
        currentListing.value.current_step = 5

        await sendNotification({
            type: 'approve_payment',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Payment Invoice approved.')
    } catch (e) {
        console.error(e); toast('Failed to approve payment invoice.', 'error')
    }
}
async function denyPayment() {
    if (!currentListing.value?.id) return
    try {
        const { error } = await supabase
            .from('listings')
            .update({ status: 'pendingagent', current_step: 4, awaiting_admin: false })
            .eq('id', currentListing.value.id)
        if (error) throw error
        currentListing.value.status = 'pendingagent'
        currentListing.value.current_step = 4

        await sendNotification({
            type: 'deny_payment',
            forAdmin: false,
            listingId: currentListing.value.id,
            userId: currentListing.value.uid
        })

        toast('Payment Invoice denied.')
    } catch (e) {
        console.error(e); toast('Failed to deny Payment Invoice.', 'error')
    }
}

/* ---------- mount ---------- */
onMounted(fetchListing)
onMounted(fetchListing)
watch(filesPage, (p) => fetchBookingFiles(p))
</script>


<style scoped>
.position-absolute.left-0 {
    left: 0;
}
</style>
